import { Link } from 'react-router-dom';
import { Car, Phone, Mail, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Pricing & Packages', path: '/pricing' },
    { name: 'Fare Calculator', path: '/calculator' },
    { name: 'Gallery', path: '/gallery' },
  ];

  const services = [
    'Airport Transfer',
    'Outstation Trips',
    'Local Rides',
    'Tour Packages',
  ];

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              {/* Option 1: Use your own logo image */}
              <img 
                src="/images/logo.png" 
                alt="Explore Destination Cab Logo" 
                className="h-12 w-12 object-contain"
              />
              {/* Option 2: Keep the icon - Comment out the img tag above and uncomment below */}
              {/* <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2.5 rounded-xl">
                <Car className="w-6 h-6 text-white" />
              </div> */}
              <div>
                <h3 className="text-xl font-bold text-white">Explore Destination</h3>
                <p className="text-xs text-orange-500 font-semibold">Premium Cab Service</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed">
              Your trusted cab service from Dehradun, offering safe, comfortable, and affordable taxi services across India.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors duration-300"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="#"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="#"
                data-testid="footer-twitter"
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-blue-400 flex items-center justify-center transition-colors duration-300"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm hover:text-orange-500 transition-colors duration-300 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/about"
                  data-testid="footer-link-about"
                  className="text-sm hover:text-orange-500 transition-colors duration-300 inline-block"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="text-sm hover:text-orange-500 transition-colors duration-300 inline-block"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service} className="text-sm flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <a
                    href="tel:+916396867682"
                    data-testid="footer-phone-1"
                    className="block hover:text-orange-500 transition-colors duration-300"
                  >
                    +91-6396867682
                  </a>
                  <a
                    href="tel:+918859202773"
                    data-testid="footer-phone-2"
                    className="block hover:text-orange-500 transition-colors duration-300"
                  >
                    +91-8859202773
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:exploredcs@gmail.com"
                  data-testid="footer-email"
                  className="text-sm hover:text-orange-500 transition-colors duration-300"
                >
                  exploredcs@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  Lower Nehrugram, Pipal Chowk<br />
                  Near Church, Dehradun
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-center md:text-left">
              © {new Date().getFullYear()} Explore Destination Cab. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-orange-500 transition-colors duration-300">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-orange-500 transition-colors duration-300">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
